import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MailX, Shield, ShieldOff, Trash2 } from 'lucide-react';
import { adminAPI } from '../api/endpoints';
import { Badge, ConfirmDialog, Table, type Column } from '../components/Common';
import type { AdminUser } from '../types';

const TIERS = ['free', 'basic', 'premium', 'enterprise'] as const;

export function AdminUsersPage() {
  const queryClient = useQueryClient();
  const [deleteTarget, setDeleteTarget] = useState<AdminUser | null>(null);
  const [adminToggleTarget, setAdminToggleTarget] = useState<AdminUser | null>(null);
  const [revokeGmailTarget, setRevokeGmailTarget] = useState<AdminUser | null>(null);

  const { data: usersData } = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => adminAPI.getUsers({ limit: 200 }).then((r) => r.data),
  });

  const updateUser = useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: { tier?: string; is_admin?: boolean } }) =>
      adminAPI.updateUser(userId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin'] });
      setAdminToggleTarget(null);
    },
  });

  const deleteUser = useMutation({
    mutationFn: (userId: string) => adminAPI.deleteUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin'] });
      setDeleteTarget(null);
    },
  });

  const revokeGmail = useMutation({
    mutationFn: (userId: string) => adminAPI.revokeGmail(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin'] });
      setRevokeGmailTarget(null);
    },
  });

  const columns: Column<AdminUser>[] = [
    {
      key: 'email',
      label: 'Email',
      sortable: true,
      width: '280px',
      render: (user) => <span className="font-medium">{user.email}</span>,
    },
    {
      key: 'tier',
      label: 'Tier',
      sortable: true,
      width: '140px',
      render: (user) => (
        <select
          value={user.tier}
          onChange={(e) => updateUser.mutate({ userId: user.id, data: { tier: e.target.value } })}
          onClick={(e) => e.stopPropagation()}
          className="input py-1 px-2 text-xs w-28"
        >
          {TIERS.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      ),
    },
    {
      key: 'is_admin',
      label: 'Admin',
      sortable: true,
      width: '100px',
      render: (user) =>
        user.is_admin ? <Badge variant="primary">Admin</Badge> : <Badge variant="default">User</Badge>,
    },
    {
      key: 'gmail_permissions_granted',
      label: 'Gmail',
      width: '100px',
      render: (user) =>
        user.gmail_permissions_granted ? (
          <Badge variant="success">Connected</Badge>
        ) : (
          <Badge variant="default">No</Badge>
        ),
    },
    {
      key: 'created_at',
      label: 'Joined',
      sortable: true,
      width: '120px',
      render: (user) => (
        <span className="text-dark-text-secondary">{new Date(user.created_at).toLocaleDateString()}</span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      width: '100px',
      render: (user) => (
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setAdminToggleTarget(user);
            }}
            className="p-1.5 rounded-lg text-dark-text-secondary hover:text-primary-400 hover:bg-dark-elevated transition-colors"
            title={user.is_admin ? 'Remove admin' : 'Make admin'}
          >
            {user.is_admin ? <ShieldOff className="w-4 h-4" /> : <Shield className="w-4 h-4" />}
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (user.gmail_permissions_granted) setRevokeGmailTarget(user);
            }}
            className={`p-1.5 rounded-lg transition-colors ${user.gmail_permissions_granted ? 'text-dark-text-secondary hover:text-orange-400 hover:bg-dark-elevated cursor-pointer' : 'text-dark-text-secondary/30 cursor-not-allowed'}`}
            title={user.gmail_permissions_granted ? 'Revoke Gmail permissions' : 'Gmail not connected'}
            disabled={!user.gmail_permissions_granted}
          >
            <MailX className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setDeleteTarget(user);
            }}
            className="p-1.5 rounded-lg text-dark-text-secondary hover:text-red-400 hover:bg-dark-elevated transition-colors"
            title="Delete user"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="h-full flex flex-col animate-fadeIn">
    <div className="max-w-(--breakpoint-xl) mx-auto w-full p-6 flex-1 flex flex-col min-h-0 space-y-8">
      <h1 className="text-2xl font-bold text-dark-text-primary tracking-tight">Users</h1>

      <div className="card animate-scaleIn flex flex-col min-h-0">
        <div className="px-4 py-3 border-b border-dark-border">
          <h2 className="text-lg font-semibold text-dark-text-primary">
            Users {usersData && <span className="text-dark-text-secondary font-normal">({usersData.total})</span>}
          </h2>
        </div>
        <Table
          data={usersData?.users ?? []}
          columns={columns}
          searchable={true}
          searchPlaceholder="Search users..."
          emptyMessage="No users found"
        />
      </div>

      <ConfirmDialog
        isOpen={!!adminToggleTarget}
        onClose={() => setAdminToggleTarget(null)}
        onConfirm={() =>
          adminToggleTarget &&
          updateUser.mutate({ userId: adminToggleTarget.id, data: { is_admin: !adminToggleTarget.is_admin } })
        }
        title={adminToggleTarget?.is_admin ? 'Remove admin' : 'Make admin'}
        message={
          adminToggleTarget?.is_admin
            ? `Are you sure you want to remove admin privileges from ${adminToggleTarget?.email}?`
            : `Are you sure you want to grant admin privileges to ${adminToggleTarget?.email}?`
        }
        confirmLabel={adminToggleTarget?.is_admin ? 'Remove admin' : 'Make admin'}
        isLoading={updateUser.isPending}
        danger={adminToggleTarget?.is_admin}
      />

      <ConfirmDialog
        isOpen={!!revokeGmailTarget}
        onClose={() => setRevokeGmailTarget(null)}
        onConfirm={() => revokeGmailTarget && revokeGmail.mutate(revokeGmailTarget.id)}
        title="Revoke Gmail permissions"
        message={`Are you sure you want to revoke Gmail read/write permissions for ${revokeGmailTarget?.email}?`}
        confirmLabel="Revoke"
        isLoading={revokeGmail.isPending}
        danger
      />

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteUser.mutate(deleteTarget.id)}
        title="Delete user"
        message={`Are you sure you want to delete ${deleteTarget?.email}? This will permanently remove their account and all associated data.`}
        confirmLabel="Delete"
        isLoading={deleteUser.isPending}
        danger
      />
    </div>
    </div>
  );
}
